import React from 'react';
import { arrayBuilderPages } from '~/platform/forms-system/src/js/patterns/array-builder';
import {
  arrayBuilderItemFirstPageTitleUI,
  arrayBuilderItemSubsequentPageTitleUI,
  arrayBuilderYesNoUI,
  arrayBuilderYesNoSchema,
  radioUI,
  radioSchema,
  fullNameUI,
  fullNameSchema,
  selectUI,
  textUI,
  checkboxUI,
  checkboxSchema,
  currentOrPastDateUI,
  currentOrPastDateSchema,
  titleUI,
  yesNoUI,
  yesNoSchema,
} from 'platform/forms-system/src/js/web-component-patterns';
import get from 'platform/utilities/data/get';
import titleCase from 'platform/utilities/data/titleCase';
import { replaceStrValues } from 'platform/shared/health-apps';
import {
  STATE_NAMES,
  STATE_VALUES,
  COUNTRY_NAMES,
  COUNTRY_VALUES,
  previousMarriageEndOptions,
} from '../../../utils/labels';
import { handleVeteranMaxMarriagesAlert } from '../../../components/FormAlerts';
import { customAddressSchema, customTextSchema } from '../../definitions';
import { validations } from '../../validations';
import content from '../../../utils/content.json';

const showVeteranMarriagePage = formData =>
  formData.claimantRelationship === 'SURVIVING_SPOUSE' &&
  formData?.['view:hasAnyPreviousMarriages'] === true &&
  formData.hadPreviousMarriages?.veteran === true;

/**
 * Pages for Veteran's previous marriages (array-builder)
 */

function introDescription() {
  return (
    <div>
      <p className="vads-u-margin-top--0">
        Next we’ll ask you about the Veteran’s previous marriages. You may add
        up to 2 marriages.
      </p>
    </div>
  );
}
/** @type {ArrayBuilderOptions} */
// arrayPath is veteranMarriages because it's the Veteran's marriages being collected
const options = {
  arrayPath: 'veteranMarriages',
  nounSingular: content['household-previous-marriages-noun-singular'],
  nounPlural: content['household-previous-marriages-noun-plural'],
  required: false,
  maxItems: 2,
  isItemIncomplete: item => !item?.spouseFullName || !item?.dateOfMarriage,
  text: {
    cancelAddTitle: replaceStrValues(
      content['array-cancel-add-title'],
      `${content['household-previous-marriages-noun-singular']}?`,
    ),
    cancelEditTitle: replaceStrValues(
      content['array-cancel-edit-title'],
      `${content['household-previous-marriages-noun-singular']}?`,
    ),
    cancelAddDescription: replaceStrValues(
      content['household-previous-marriages-cancel-add-description'],
      content['household-previous-marriages-veteran-previous-marriage-suffix'],
    ),
    cancelAddReviewDescription: replaceStrValues(
      content['household-previous-marriages-cancel-add-description'],
      content['household-previous-marriages-veteran-previous-marriage-suffix'],
    ),
    cancelEditDescription:
      content['household-previous-marriages-cancel-edit-description'],
    cancelEditReviewDescription:
      content['household-previous-marriages-cancel-edit-description'],
    cancelYes: content['array-cancel-add-yes'],
    cancelAddYes: content['array-cancel-add-yes'],
    cancelNo: content['array-cancel-add-no'],
    cancelAddNo: content['array-cancel-add-no'],
    cancelEditYes: content['array-cancel-edit-yes'],
    cancelEditNo: content['array-cancel-edit-no'],
    deleteDescription: replaceStrValues(
      content['household-previous-marriages-delete-description'],
      [
        content.the,
        content[
          'household-previous-marriages-veteran-previous-marriage-suffix'
        ],
      ],
    ),
    deleteNeedAtLeastOneDescription: content['array-empty-hint'],
    deleteNo: content['array-delete-no'],
    deleteTitle: replaceStrValues(
      content['array-delete-title'],
      `${content['household-previous-marriages-noun-singular']}?`,
    ),
    deleteYes: content['array-delete-yes'],
    alertMaxItems: handleVeteranMaxMarriagesAlert,
    getItemName: item => {
      const { first, middle, last, suffix } = get('spouseFullName', item) || {};
      const name = [first, middle, last, suffix].filter(Boolean).join(' ');
      return name || 'Previous marriage';
    },
    summaryTitle: "Review the Veteran's previous marriages",
  },
};

const introPage = {
  uiSchema: {
    ...titleUI({
      title: content['household-veteran-previous-marriages-intro-title'],
      nounSingular: options.nounSingular,
      nounPlural: options.nounPlural,
    }),
    'ui:description': introDescription,
  },
  schema: {
    type: 'object',
    properties: {},
  },
};

/** @returns {PageSchema} */
const summaryPage = {
  uiSchema: {
    'view:wasMarriedBefore': arrayBuilderYesNoUI(
      options,
      {
        title: content['household-veteran-previous-marriages-summary-add'],
        hint: content['array-empty-hint'],
      },
      {
        title:
          content['household-veteran-previous-marriages-summary-add-another'],
        hint: content['array-empty-hint'],
      },
    ),
    veteranHasAdditionalMarriages: {
      ...yesNoUI({
        title:
          content['household-veteran-previous-marriages-summary-additional'],
      }),
      'ui:required': formData => formData?.veteranMarriages?.length === 2,
      'ui:options': {
        hideIf: formData =>
          !formData?.veteranMarriages || formData?.veteranMarriages?.length < 2,
      },
    },
  },
  schema: {
    type: 'object',
    properties: {
      'view:wasMarriedBefore': arrayBuilderYesNoSchema,
      veteranHasAdditionalMarriages: yesNoSchema,
    },
    required: ['view:wasMarriedBefore'],
  },
};

const namePage = {
  uiSchema: {
    ...arrayBuilderItemFirstPageTitleUI({
      title: content['household-veteran-previous-marriages-name-title'],
      nounSingular: options.nounSingular,
      nounPlural: options.nounPlural,
      lowerCase: false,
    }),
    spouseFullName: fullNameUI(),
  },
  schema: {
    type: 'object',
    properties: {
      spouseFullName: fullNameSchema,
    },
    required: ['spouseFullName'],
  },
};

/** @returns {PageSchema} */
const marriageDatePlacePage = {
  uiSchema: {
    ...arrayBuilderItemSubsequentPageTitleUI(
      replaceStrValues(
        content['household-previous-marriages-date-location-title'],
        content.they,
      ),
    ),
    dateOfMarriage: currentOrPastDateUI({
      title: content['household-previous-marriages-date-location-date'],
      monthSelect: false,
      'ui:description': content['date-description'],
      required: formData => !formData['view:marriageDate'],
    }),
    marriedOutsideUs: checkboxUI({
      title: replaceStrValues(
        content['household-previous-marriages-date-location-outside-us'],
        titleCase(content.they),
      ),
    }),
    locationOfMarriage: {
      city: {
        ...textUI(content['address-city']),
        'ui:errorMessages': {
          required: content['address-city-error'],
        },
      },
      state: {
        ...selectUI(content['address-state'], STATE_VALUES, STATE_NAMES),
        'ui:required': (formData, index) => {
          const item = formData?.veteranMarriages?.[index];
          const currentPageData = formData;
          return !(item?.marriedOutsideUs || currentPageData?.marriedOutsideUs);
        },
        'ui:options': {
          hideIf: (formData, index) => {
            const item = formData?.veteranMarriages?.[index];
            const currentPageData = formData;
            return item?.marriedOutsideUs || currentPageData?.marriedOutsideUs;
          },
        },
        'ui:errorMessages': {
          required: content['address-state-error'],
        },
      },
      otherCountry: {
        ...selectUI(content['address-country'], COUNTRY_VALUES, COUNTRY_NAMES),
        'ui:required': (formData, index) => {
          const item = formData?.veteranMarriages?.[index];
          const currentPageData = formData;
          return item?.marriedOutsideUs || currentPageData?.marriedOutsideUs;
        },
        'ui:options': {
          hideIf: (formData, index) => {
            const item = formData?.veteranMarriages?.[index];
            const currentPageData = formData;
            return !(
              item?.marriedOutsideUs || currentPageData?.marriedOutsideUs
            );
          },
          labels: COUNTRY_VALUES.reduce((acc, value, idx) => {
            acc[value] = COUNTRY_NAMES[idx];
            return acc;
          }, {}),
        },
        'ui:errorMessages': {
          required: content['address-country-error'],
        },
      },
    },
  },
  schema: {
    type: 'object',
    required: ['locationOfMarriage', 'dateOfMarriage'],
    properties: {
      dateOfMarriage: currentOrPastDateSchema,
      marriedOutsideUs: checkboxSchema,
      locationOfMarriage: customAddressSchema,
    },
  },
};

/** @returns {PageSchema} */
const endedPage = {
  uiSchema: {
    reasonForSeparation: radioUI({
      title: content['household-previous-marriages-separation-title'],
      labels: previousMarriageEndOptions,
      labelHeaderLevel: 3,
    }),
    separationExplanation: textUI({
      title: content['household-previous-marriages-separation-explanation'],
      expandUnder: 'reasonForSeparation',
      expandUnderCondition: field => field === 'OTHER',
      required: (formData, index) => {
        const item = formData?.veteranMarriages?.[index];
        const currentPageData = formData;
        return (
          item?.reasonForSeparation === 'OTHER' ||
          currentPageData?.reasonForSeparation === 'OTHER'
        );
      },
      errorMessages: {
        required:
          content['household-previous-marriages-separation-explanation-error'],
      },
    }),
  },
  schema: {
    type: 'object',
    properties: {
      reasonForSeparation: radioSchema(Object.keys(previousMarriageEndOptions)),
      separationExplanation: customTextSchema,
    },
    required: ['reasonForSeparation'],
  },
};

/** @returns {PageSchema} */
const marriageEndDateLocationPage = {
  uiSchema: {
    ...arrayBuilderItemSubsequentPageTitleUI(
      replaceStrValues(
        content['household-previous-marriages-end-date-location-title'],
        content.their,
      ),
    ),
    dateOfSeparation: currentOrPastDateUI({
      title: 'Date marriage ended',
      monthSelect: false,
      'ui:description': content['date-description'],
      required: formData => !formData['view:dateOfSeparation'],
      validations: [validations.isAfterPreviousMarriageStartDate],
    }),
    marriageEndedOutsideUs: checkboxUI({
      title: replaceStrValues(
        content['household-previous-marriages-end-date-location-outside-us'],
        titleCase(content.their),
      ),
    }),
    locationOfSeparation: {
      city: {
        ...textUI(content['address-city']),
        'ui:errorMessages': {
          required: content['address-city-error'],
        },
      },
      state: {
        ...selectUI(content['address-state'], STATE_VALUES, STATE_NAMES),
        'ui:required': (formData, index) => {
          const item = formData?.veteranMarriages?.[index];
          const currentPageData = formData;
          return !(
            item?.marriageEndedOutsideUs ||
            currentPageData?.marriageEndedOutsideUs
          );
        },
        'ui:options': {
          hideIf: (formData, index) => {
            const item = formData?.veteranMarriages?.[index];
            const currentPageData = formData;
            return (
              item?.marriageEndedOutsideUs ||
              currentPageData?.marriageEndedOutsideUs
            );
          },
        },
        'ui:errorMessages': {
          required: content['address-state-error'],
        },
      },
      otherCountry: {
        ...selectUI(content['address-country'], COUNTRY_VALUES, COUNTRY_NAMES),
        'ui:required': (formData, index) => {
          const item = formData?.veteranMarriages?.[index];
          const currentPageData = formData;
          return (
            item?.marriageEndedOutsideUs ||
            currentPageData?.marriageEndedOutsideUs
          );
        },
        'ui:options': {
          hideIf: (formData, index) => {
            const item = formData?.veteranMarriages?.[index];
            const currentPageData = formData;
            return !(
              item?.marriageEndedOutsideUs ||
              currentPageData?.marriageEndedOutsideUs
            );
          },
          labels: COUNTRY_VALUES.reduce((acc, value, idx) => {
            acc[value] = COUNTRY_NAMES[idx];
            return acc;
          }, {}),
        },
        'ui:errorMessages': {
          required: content['address-country-error'],
        },
      },
    },
  },
  schema: {
    type: 'object',
    required: ['locationOfSeparation', 'dateOfSeparation'],
    properties: {
      dateOfSeparation: currentOrPastDateSchema,
      marriageEndedOutsideUs: checkboxSchema,
      locationOfSeparation: customAddressSchema,
    },
  },
};

export const veteranMarriagesPages = arrayBuilderPages(
  options,
  pageBuilder => ({
    veteranMarriagesIntro: pageBuilder.introPage({
      title: content['household-veteran-previous-marriages-intro-title'],
      path: 'household/veteran-previous-marriages',
      depends: formData => showVeteranMarriagePage(formData),
      uiSchema: introPage.uiSchema,
      schema: introPage.schema,
    }),
    veteranMarriagesSummary: pageBuilder.summaryPage({
      title: content['household-veteran-previous-marriages-summary-add'],
      path: 'household/veteran-previous-marriages/add',
      depends: formData => showVeteranMarriagePage(formData),
      uiSchema: summaryPage.uiSchema,
      schema: summaryPage.schema,
    }),
    veteranPreviousSpouseName: pageBuilder.itemPage({
      title: content['household-veteran-previous-marriages-name-title'],
      path: 'household/veteran-previous-marriages/:index/spouse-name',
      depends: formData => showVeteranMarriagePage(formData),
      uiSchema: namePage.uiSchema,
      schema: namePage.schema,
    }),
    veteranMarriageDatePlace: pageBuilder.itemPage({
      title: replaceStrValues(
        content['household-previous-marriages-date-location-title'],
        content.they,
      ),
      path: 'household/veteran-previous-marriages/:index/marriage-date-place',
      depends: formData => showVeteranMarriagePage(formData),
      uiSchema: marriageDatePlacePage.uiSchema,
      schema: marriageDatePlacePage.schema,
    }),
    veteranMarriageEnded: pageBuilder.itemPage({
      title: content['household-previous-marriages-separation-title'],
      path: 'household/veteran-previous-marriages/:index/marriage-ended',
      depends: formData => showVeteranMarriagePage(formData),
      uiSchema: endedPage.uiSchema,
      schema: endedPage.schema,
    }),
    veteranMarriageEndDateLocation: pageBuilder.itemPage({
      title: replaceStrValues(
        content['household-previous-marriages-end-date-location-title'],
        content.their,
      ),
      path: 'household/veteran-previous-marriages/:index/marriage-end-date-location',
      depends: formData => showVeteranMarriagePage(formData),
      uiSchema: marriageEndDateLocationPage.uiSchema,
      schema: marriageEndDateLocationPage.schema,
    }),
  }),
);

export default veteranMarriagesPages;

export { options };
